import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, num, dt, rp } from "@/lib/api";
import { PageHeader, Card, Table, StatusBadge, Field, Sel, Money, KV, AuditTimeline, LedgerTable } from "@/components/common";
import { Modal, SaveBtn, NewBtn } from "@/components/Modal";
import { NoteDialog } from "@/components/NoteDialog";
import { LineEditor, blankLine, toPayload } from "@/components/LineEditor";
import { useAuth } from "@/context/AuthContext";
import { today } from "@/lib/api";

const ProductionForm = ({ open, setOpen, onDone }) => {
  const { meta, myLocs, warehouse } = useAuth();
  const recipes = meta.recipes.filter((r) => r.kind === "production" && r.active !== false);
  const [f, setF] = useState({ date: today(), source_id: warehouse?.id, destination_id: warehouse?.id, recipe_id: "", batches: 1, notes: "" });
  const [inputs, setInputs] = useState([blankLine()]);
  const [output, setOutput] = useState([blankLine()]);
  const applyRecipe = (rid, n = f.batches) => {
    const rc = recipes.find((r) => r.id === rid);
    setF((x) => ({ ...x, recipe_id: rid, batches: n }));
    if (!rc) return;
    setInputs(rc.ingredients.map((i) => ({ item_id: i.item_id, qty: i.qty * n, unit: i.unit })));
    setOutput([{ item_id: rc.output_item_id, qty: rc.output_qty * n, unit: rc.output_unit }]);
  };
  const save = async () => {
    try {
      const out = toPayload(output)[0];
      const { data } = await api.post("/productions", { ...f, recipe_id: f.recipe_id || null, inputs: toPayload(inputs), output: out });
      toast.success(`${data.code} selesai`); setOpen(false); onDone(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open={open} onOpenChange={setOpen} title="Produksi baru" wide testid="production-modal">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Tanggal"><input type="date" className="inp" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Resep / BOM"><Sel testid="production-recipe-select" value={f.recipe_id} onChange={(v) => applyRecipe(v)} placeholder="Manual" options={recipes.map((r) => ({ value: r.id, label: r.name }))} /></Field>
        <Field label="Jumlah batch"><input type="number" min="0" step="any" className="inp num" value={f.batches} onChange={(e) => applyRecipe(f.recipe_id, Number(e.target.value))} data-testid="production-batches-input" /></Field>
        <Field label="Lokasi bahan"><Sel testid="production-source-select" value={f.source_id} onChange={(v) => setF({ ...f, source_id: v })} options={myLocs.map((l) => ({ value: l.id, label: l.name }))} /></Field>
        <Field label="Hasil ke" className="md:col-start-4"><Sel testid="production-dest-select" value={f.destination_id} onChange={(v) => setF({ ...f, destination_id: v })} options={meta.locations.map((l) => ({ value: l.id, label: l.name }))} /></Field>
      </div>
      <Field label="Bahan input (A + B + C)"><LineEditor lines={inputs} setLines={setInputs} testid="prod-input" /></Field>
      <Field label="Hasil (D)"><LineEditor lines={output.slice(0, 1)} setLines={(l) => setOutput(l.length ? l.slice(0, 1) : [blankLine()])} testid="prod-output" /></Field>
      <input className="inp" placeholder="Catatan" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <SaveBtn onClick={save} testid="production-save-button">Selesaikan produksi</SaveBtn>
    </Modal>
  );
};

export default function Production() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get("/productions").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <PageHeader title="Produksi" sub="Mengubah bahan menjadi barang lain (mis. bahan + kemasan → produk jadi, 1 pouch → 10 sachet). Biaya hasil = total biaya bahan.">
        <NewBtn onClick={() => setOpen(true)} testid="new-production-button">Produksi baru</NewBtn>
      </PageHeader>
      <Card>
        <Table testid="production-table" rows={rows} onRow={(r) => nav(`/production/${r.id}`)} cols={[
          { h: "Kode", r: (r) => <span className="num text-xs font-semibold text-emerald-700">{r.code}</span> },
          { h: "Tanggal", k: "date" },
          { h: "Bahan", r: (r) => <span className="text-xs">{r.inputs.map((i) => `${i.item_name} ${num(i.qty)} ${i.unit}`).join(" + ")}</span> },
          { h: "Hasil", r: (r) => <span className="text-xs font-semibold">{num(r.output.qty)} {r.output.unit} {r.output.item_name}</span> },
          { h: "Ke", k: "destination_name" },
          { h: "Biaya", right: true, r: (r) => <Money v={r.total_cost} /> },
          { h: "Status", r: (r) => <StatusBadge s={r.status} /> },
          { h: "Oleh", r: (r) => <span className="text-xs">{r.created_by.name} · {dt(r.created_at)}</span> },
        ]} />
      </Card>
      <ProductionForm open={open} setOpen={setOpen} onDone={(id) => nav(`/production/${id}`)} />
    </div>
  );
}

export function ProductionDetail() {
  const { id } = useParams();
  const { locById, can } = useAuth();
  const [d, setD] = useState(null);
  const [rev, setRev] = useState(false);
  const load = useCallback(() => api.get(`/productions/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const p = d.production;
  const reverse = async (note) => { try { await api.post(`/productions/${id}/reverse`, { note }); toast.success("Produksi dibalik"); setRev(false); } catch (e) { toast.error(errMsg(e)); } load(); };
  return (
    <div>
      <PageHeader title={<span className="flex items-center gap-3"><span className="num">{p.code}</span><StatusBadge s={p.status} /></span>} sub={`${p.source_name} → ${p.destination_name}`}>
        {p.status === "completed" && can("production.do") && <button onClick={() => setRev(true)} className="h-9 px-3 rounded-md border border-rose-300 text-rose-700 text-sm font-semibold" data-testid="reverse-production-button">Balik produksi</button>}
      </PageHeader>
      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Card title="Detail"><KV items={[["Tanggal", p.date], ["Oleh", p.created_by.name], ["Total biaya", rp(p.total_cost), "m"], ["Biaya/unit hasil", `${rp(p.output.unit_cost)}/${p.output.base_unit}`, "m"], ["Catatan", p.notes]]} /></Card>
          <Card title="Bahan → hasil">
            <Table rows={[...p.inputs.map((i) => ({ ...i, role: "Input" })), { ...p.output, role: "Output" }]} cols={[
              { h: "Peran", k: "role" }, { h: "Barang", k: "item_name" }, { h: "Qty", right: true, r: (l) => <span className="num">{num(l.qty)} {l.unit}</span> },
              { h: "Qty stok", right: true, r: (l) => <span className="num">{num(l.base_qty)} {l.base_unit}</span> }, { h: "Nilai", right: true, r: (l) => <Money v={l.total} /> }]} />
          </Card>
          <Card title="Posting ledger"><LedgerTable rows={d.ledger} locById={locById} /></Card>
        </div>
        <Card title="Jejak audit" className="h-fit"><AuditTimeline items={d.audit} /></Card>
      </div>
      <NoteDialog open={rev} onOpenChange={setRev} danger title="Balik produksi" desc="Hasil produksi dikeluarkan dan bahan dikembalikan melalui entri reversal." confirm="Balik" onConfirm={reverse} />
    </div>
  );
}
